/**
 * HTTP transport - TypeScript port of
 * frontend/public/js/auth/api-client.js with parity:
 *
 *  - Attaches `Authorization: Bearer <jwt>` from tokenManager.
 *  - On 401, runs a one-shot silent refresh and retries once.
 *  - On still-401, redirects to /login (or /admin-login on staff pages).
 *  - On 403, throws an ApiError with status=403 and parsed body.
 *  - On usage-mutating endpoints (chatbot, AI predictions, plaid sync,
 *    etc.), broadcasts a `bokap-usage` channel message so other tabs
 *    re-fetch their quota counters.
 *
 * This is the ONLY transport in the React codebase. Pages must not call
 * `fetch('/api/...')` directly.
 */

import { tokenManager } from './tokenManager'

export class ApiError extends Error {
  status: number
  body?: unknown

  constructor(message: string, status: number, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

const USAGE_MUTATING_PATHS: RegExp[] = [
  /^\/api\/chatbot(\/|$)/,
  /^\/api\/budget\/(ai-recommendations|recurring-insights)/,
  /^\/api\/statements\/(predict-batch|predict-coa-batch|upload|confirm-upload)/,
  /^\/api\/plaid\/(exchange_public_token|sync|confirm-plaid)/,
  /^\/api\/notifications\/.*\/confirm/,
]

let usageChannel: BroadcastChannel | null = null
try {
  usageChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('bokap-usage') : null
} catch {
  /* unsupported */
}

function broadcastUsageIfRelevant(method: string, path: string): void {
  if (method === 'GET') return
  if (!USAGE_MUTATING_PATHS.some((rx) => rx.test(path))) return
  try {
    usageChannel?.postMessage({ type: 'refresh', path })
  } catch {
    /* ignore */
  }
}

type Query = Record<string, string | number | boolean | null | undefined>

type RequestOpts = {
  body?: unknown
  query?: Query
  signal?: AbortSignal
  headers?: Record<string, string>
}

function buildInit(method: string, body: unknown, headers?: Record<string, string>): RequestInit {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(headers ?? {}),
    },
  }
  if (body !== null && body !== undefined) {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json'
    init.body = JSON.stringify(body)
  }
  const token = tokenManager.get()
  if (token) (init.headers as Record<string, string>).Authorization = `Bearer ${token}`
  return init
}

function appendQuery(path: string, query?: Query): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === '') continue
    qs.append(k, String(v))
  }
  const s = qs.toString()
  if (!s) return path
  return path + (path.includes('?') ? '&' : '?') + s
}

function isStaffPage(): boolean {
  if (typeof location === 'undefined') return false
  const p = location.pathname
  return (
    p.startsWith('/platform-admin') ||
    p.startsWith('/admin-login') ||
    p.startsWith('/app/platform') ||
    p.startsWith('/app/admin')
  )
}

function isLoginPage(): boolean {
  if (typeof location === 'undefined') return false
  const p = location.pathname
  return (
    p.startsWith('/login') ||
    p.startsWith('/admin-login') ||
    p.startsWith('/app/login') ||
    p.startsWith('/app/admin-login') ||
    p.startsWith('/signup') ||
    p.startsWith('/forgot-password') ||
    p.startsWith('/reset-password') ||
    p.startsWith('/verify-email') ||
    p.startsWith('/accept-invite') ||
    p.startsWith('/session-expired') ||
    p.startsWith('/logout')
  )
}

// Public, unauthenticated routes. A 401 from a fetch fired on these pages
// must NOT clear the token or redirect to /login - the recipient is
// supposed to be able to view the page without an account, and a
// background hook (e.g. useReportBrand → useTenantSettings) accidentally
// hitting an authed endpoint would otherwise flash the page and bounce
// the visitor to /login.
function isPublicPage(): boolean {
  if (typeof location === 'undefined') return false
  const p = location.pathname
  return (
    p.startsWith('/chat/share/') ||
    p.startsWith('/pay/') ||
    p.startsWith('/portal/') ||
    p.startsWith('/plaid/oauth-callback')
  )
}

// Auth endpoints the user hits BEFORE they have a session - don't run the
// silent-refresh / redirect-on-401 dance for these, or the login page gets
// bounced and the real "bad credentials" error is hidden behind a generic
// "Unauthorized".
const UNAUTH_PATHS: RegExp[] = [
  /^\/api\/auth\/cognito\/(signin|confirm|session|confirm-device)/,
  /^\/api\/auth\/admin\/cognito\/(signin|confirm|session)/,
  /^\/api\/auth\/(login|signup|forgot-password|reset-password|verify-email|resend-verification|refresh)/,
  /^\/api\/users\/invitations\//,
]

async function request<T>(method: string, path: string, opts: RequestOpts = {}): Promise<T> {
  const url = appendQuery(path, opts.query)
  const isUnauthEndpoint = UNAUTH_PATHS.some((rx) => rx.test(path))

  let res = await fetch(url, { ...buildInit(method, opts.body, opts.headers), signal: opts.signal })

  if (res.status === 401 && !isUnauthEndpoint) {
    const ok = await tokenManager.refreshNow()
    if (ok) {
      res = await fetch(url, { ...buildInit(method, opts.body, opts.headers), signal: opts.signal })
    }
  }

  if (res.status === 401) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      /* ignore */
    }
    if (!isUnauthEndpoint && !isPublicPage()) {
      tokenManager.clear()
      if (typeof location !== 'undefined' && !isLoginPage()) {
        const next = encodeURIComponent(location.pathname + location.search)
        location.href = isStaffPage() ? '/admin-login' : `/login?next=${next}`
      }
    }
    const msg =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'Unauthorized'
    throw new ApiError(msg, 401, body)
  }

  if (res.status === 403) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      /* ignore */
    }
    // Surface the server's `error` field so users see *why* the action was
    // refused (self-approval blocked, missing permission code, etc.) instead
    // of a generic "Forbidden". Falls back to "Forbidden" only when the
    // server didn't return a structured body.
    const msg =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'Forbidden'
    throw new ApiError(msg, 403, body)
  }

  if (!res.ok) {
    let msg = `Request failed (${res.status})`
    let body: unknown
    try {
      body = await res.json()
      if (body && typeof body === 'object' && 'error' in body) {
        msg = String((body as { error: unknown }).error)
      }
    } catch {
      /* ignore */
    }
    // 402 = quota / billing / AI Gateway limit reached. Surface a global
    // event so the LimitReachedListener can render a single popup, while
    // still throwing the ApiError so the calling mutation knows it failed
    // and can roll back optimistic state.
    if (res.status === 402 && typeof window !== 'undefined') {
      try {
        window.dispatchEvent(
          new CustomEvent('bokap:limit-reached', { detail: { path, body, message: msg } }),
        )
      } catch {
        /* ignore */
      }
    }
    // 409 with code: 'STALE_VERSION' = optimistic-concurrency conflict.
    // Surface a global event so a top-level listener can render a single
    // "this was changed by someone else, reload?" toast/dialog, while
    // still throwing so the calling mutation can stop and the form can
    // re-fetch.
    if (
      res.status === 409 &&
      body && typeof body === 'object' &&
      'code' in body && (body as { code: unknown }).code === 'STALE_VERSION' &&
      typeof window !== 'undefined'
    ) {
      try {
        window.dispatchEvent(
          new CustomEvent('bokap:stale-version', { detail: { path, body, message: msg } }),
        )
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(msg, res.status, body)
  }

  broadcastUsageIfRelevant(method, path)

  if (res.status === 204) return undefined as T
  const ct = res.headers.get('content-type') || ''
  if (ct.includes('application/json')) return (await res.json()) as T
  return (await res.text()) as unknown as T
}

export const api = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('POST', path, { ...opts, body: body ?? {} }),
  put: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('PUT', path, { ...opts, body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('PATCH', path, { ...opts, body: body ?? {} }),
  del: <T>(path: string, opts?: RequestOpts) => request<T>('DELETE', path, opts),
}

/**
 * True when the error came from the optimistic-concurrency helper - i.e.
 * the row was changed by someone else after the form loaded it. Forms
 * should detect this and re-fetch + show the conflict UI rather than
 * treating it as a generic 409.
 */
export function isStaleVersionError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  if (err.status !== 409) return false
  const body = err.body
  return !!(body && typeof body === 'object' && 'code' in body && (body as { code: unknown }).code === 'STALE_VERSION')
}
